import type { TFunction } from 'i18next';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Image, ImageSourcePropType, StyleSheet, Text, View } from 'react-native';

import DoubleDailyBonus from '../../../assets/storeModal/vipClub/double-daily-bonus.png';
import DoubleXp from '../../../assets/storeModal/vipClub/double-xp.png';
import ExclusiveCosmetics from '../../../assets/storeModal/vipClub/exclusive-cosmetics.png';
import FreePremiumPass from '../../../assets/storeModal/vipClub/free-premium-pass.png';
import VipHero from '../../../assets/storeModal/vipClub/hero.png';
import MonthlyRenewalRewards from '../../../assets/storeModal/vipClub/monthly-renewal-rewards.png';
import OneMonth from '../../../assets/storeModal/vipClub/one-month.png';
import OneYear from '../../../assets/storeModal/vipClub/one-year.png';
import SixMonths from '../../../assets/storeModal/vipClub/six-months.png';
import VipRooms from '../../../assets/storeModal/vipClub/vip-rooms.png';
import WeeklyVipRewards from '../../../assets/storeModal/vipClub/weekly-vip-rewards.png';
import WelcomeReward from '../../../assets/storeModal/vipClub/welcome-reward.png';
import { Package } from '../../../store/types';
import { CtaButton } from '../common/CtaButton';
import { RedesignScrollView } from '../common/RedesignScrollView';
import { SHOP_SCROLL_INDICATOR } from '../common/ScrollIndicator';
import { Pressable } from '../common/Touchable';

type Benefit = {
	defaultDescription: string;
	defaultTitle: string;
	descriptionKey: string;
	image: ImageSourcePropType;
	titleKey: string;
};

const BENEFITS: Benefit[] = [
	{
		titleKey: 'T_STORE_VIP_WELCOME_REWARD',
		descriptionKey: 'T_STORE_VIP_WELCOME_REWARD_DESCRIPTION',
		defaultTitle: 'Welcome Reward',
		defaultDescription: 'A special gift when you subscribe',
		image: WelcomeReward,
	},
	{
		titleKey: 'T_STORE_VIP_WEEKLY_REWARDS',
		descriptionKey: 'T_STORE_VIP_WEEKLY_REWARDS_DESCRIPTION',
		defaultTitle: 'Weekly VIP Rewards',
		defaultDescription: 'Claim exclusive rewards every week',
		image: WeeklyVipRewards,
	},
	{
		titleKey: 'T_STORE_VIP_MONTHLY_RENEWAL_REWARDS',
		descriptionKey: 'T_STORE_VIP_MONTHLY_RENEWAL_REWARDS_DESCRIPTION',
		defaultTitle: 'Monthly Renewal Rewards',
		defaultDescription: 'Get a bonus every month',
		image: MonthlyRenewalRewards,
	},
	{
		titleKey: 'T_STORE_VIP_FREE_PREMIUM_EVENT_PASS',
		descriptionKey: 'T_STORE_VIP_FREE_PREMIUM_EVENT_PASS_DESCRIPTION',
		defaultTitle: 'Free Premium Event Pass',
		defaultDescription: 'Get premium access to all events',
		image: FreePremiumPass,
	},
	{
		titleKey: 'T_STORE_VIP_DOUBLE_XP',
		descriptionKey: 'T_STORE_VIP_DOUBLE_XP_DESCRIPTION',
		defaultTitle: 'Double XP',
		defaultDescription: 'Level up faster and earn more',
		image: DoubleXp,
	},
	{
		titleKey: 'T_STORE_VIP_ROOMS',
		descriptionKey: 'T_STORE_VIP_ROOMS_DESCRIPTION',
		defaultTitle: 'VIP Rooms',
		defaultDescription: 'Exclusive access to VIP rooms',
		image: VipRooms,
	},
	{
		titleKey: 'T_STORE_VIP_DOUBLE_DAILY_BONUS',
		descriptionKey: 'T_STORE_VIP_DOUBLE_DAILY_BONUS_DESCRIPTION',
		defaultTitle: 'Double Daily Bonus',
		defaultDescription: 'Get 2x your daily bonus',
		image: DoubleDailyBonus,
	},
	{
		titleKey: 'T_STORE_VIP_EXCLUSIVE_COSMETICS',
		descriptionKey: 'T_STORE_VIP_EXCLUSIVE_COSMETICS_DESCRIPTION',
		defaultTitle: 'Exclusive Cosmetics',
		defaultDescription: 'VIP profile frame, card back and more',
		image: ExclusiveCosmetics,
	},
];

type Props = {
	/**
	 * The donation store: fixed-duration VIP_MEMBERSHIP packages on every layout (the phone one
	 * otherwise sells the subscription), and no way off to the player's own VIP Club.
	 */
	donation?: boolean;
	isDesktop: boolean;
	onBuy: (pkg: Package) => void;
	onGoToClub: () => void;
	scale: number;
	vipPackages: Package[];
};

const vipPackageTitle = (t: TFunction, pkg: Package) => {
	if ((pkg.vipDurationDays ?? 0) >= 365)
		return t('T_STORE_VIP_PACKAGE_ONE_YEAR', { defaultValue: 'VIP 1 year' });
	if ((pkg.vipDurationDays ?? 0) >= 180)
		return t('T_STORE_VIP_PACKAGE_SIX_MONTHS', { defaultValue: 'VIP 6 months' });
	return t('T_STORE_VIP_PACKAGE_ONE_MONTH', { defaultValue: 'VIP 1 month' });
};

const vipPackageArtwork = (pkg: Package) => {
	if ((pkg.vipDurationDays ?? 0) >= 365) return OneYear;
	if ((pkg.vipDurationDays ?? 0) >= 180) return SixMonths;
	return OneMonth;
};

const formatPackagePrice = (pkg: Package) => `€${Number(pkg.price).toFixed(2)}`;

const StoreVipContent = ({
	donation = false,
	isDesktop,
	onBuy,
	onGoToClub,
	scale,
	vipPackages,
}: Props) =>
	isDesktop ? (
		<DesktopVipContent
			donation={donation}
			onBuy={onBuy}
			onGoToClub={onGoToClub}
			scale={scale}
			vipPackages={vipPackages}
		/>
	) : donation ? (
		<MobileDonationVipContent onBuy={onBuy} scale={scale} vipPackages={vipPackages} />
	) : (
		<MobileVipContent onGoToClub={onGoToClub} scale={scale} />
	);

/**
 * The phone layout of the donation store's VIP tab: the fixed-duration packages as rows in the
 * chips tab's shape, then what the recipient gets. One-time purchases only - the phone's own VIP
 * tab sells the subscription, which cannot be donated.
 */
const MobileDonationVipContent = ({
	onBuy,
	scale,
	vipPackages,
}: Pick<Props, 'onBuy' | 'scale' | 'vipPackages'>) => {
	const { t } = useTranslation();

	return (
		<View style={styles.root}>
			<RedesignScrollView
				style={styles.scroll}
				contentContainerStyle={[
					styles.content,
					styles.mobile,
					{ padding: 12 * scale, paddingBottom: 20 * scale, rowGap: 10 * scale },
				]}
				indicatorTheme={SHOP_SCROLL_INDICATOR}
				reserveGutter={false}
			>
				{vipPackages.map(pkg => (
					<View
						key={pkg.id}
						style={[
							styles.donationPackage,
							{ minHeight: 104 * scale, borderRadius: 10 * scale, padding: 8 * scale },
						]}
					>
						<Image
							source={vipPackageArtwork(pkg)}
							resizeMode='contain'
							style={{ width: 112 * scale, height: 88 * scale }}
						/>
						<View style={[styles.donationPackageDetails, { rowGap: 10 * scale }]}>
							<Text
								numberOfLines={1}
								style={[
									styles.desktopPackageTitle,
									{ fontSize: 24 * scale, lineHeight: 27 * scale },
								]}
							>
								{vipPackageTitle(t, pkg)}
							</Text>
							<CtaButton
								label={formatPackagePrice(pkg)}
								onPress={() => onBuy(pkg)}
								style={{ width: 168 * scale, minHeight: 36 * scale }}
								labelStyle={{ fontSize: 13 }}
							/>
						</View>
					</View>
				))}

				<Text
					style={[
						styles.benefitsHeading,
						{ fontSize: 18 * scale, lineHeight: 24 * scale, marginVertical: 8 * scale },
					]}
				>
					{t('T_STORE_VIP_BENEFITS', { defaultValue: 'VIP CLUB BENEFITS' })}
				</Text>
				<View style={[styles.benefits, { gap: 10 * scale }]}>
					{BENEFITS.map(benefit => (
						<BenefitCard key={benefit.titleKey} benefit={benefit} isDesktop={false} scale={scale} />
					))}
				</View>
			</RedesignScrollView>
		</View>
	);
};

const MobileVipContent = ({ onGoToClub, scale }: Pick<Props, 'onGoToClub' | 'scale'>) => {
	const { t } = useTranslation();

	return (
		<View style={styles.root}>
			<RedesignScrollView
				style={styles.scroll}
				contentContainerStyle={[
					styles.content,
					styles.mobile,
					{
						paddingBottom: 20 * scale,
						paddingHorizontal: 16 * scale,
						paddingTop: 0,
						rowGap: 4 * scale,
					},
				]}
				indicatorTheme={SHOP_SCROLL_INDICATOR}
				reserveGutter={false}
			>
				<View style={[styles.hero, { marginBottom: 12 * scale, rowGap: 0 }]}>
					<View style={styles.heroImageFrame}>
						<Image source={VipHero} resizeMode='contain' style={styles.heroImage} />
					</View>
					<Text style={[styles.tagline, { fontSize: 14 * scale, lineHeight: 20 * scale }]}>
						{t('T_STORE_VIP_TAGLINE', {
							defaultValue: 'MORE CHIPS. EXCLUSIVE REWARDS. PREMIUM EXPERIENCE.',
						})}
					</Text>
				</View>

				<View
					style={[
						styles.subscription,
						{ borderRadius: 16 * scale, padding: 12 * scale, rowGap: 8 * scale },
					]}
				>
					<View style={styles.priceRow}>
						<Text style={[styles.price, { fontSize: 30 * scale, lineHeight: 36 * scale }]}>
							€4.99
						</Text>
						<Text style={[styles.period, { fontSize: 18 * scale, lineHeight: 24 * scale }]}>
							{t('T_STORE_VIP_PRICE_PERIOD', { defaultValue: 'month' })}
						</Text>
					</View>
					<Text style={[styles.renewal, { fontSize: 14 * scale, lineHeight: 20 * scale }]}>
						{t('T_STORE_VIP_AUTO_RENEWAL', { defaultValue: 'Auto-renews every 30 days' })}
					</Text>
					<CtaButton
						label={t('T_STORE_VIP_SUBSCRIBE', { defaultValue: 'SUBSCRIBE' })}
						size='medium'
						style={styles.subscribeAction}
						labelStyle={{ fontSize: 22 * scale, fontWeight: '900' }}
					/>
					<Pressable accessibilityRole='button' style={styles.manageSubscription}>
						<Text
							style={[
								styles.manageSubscriptionLabel,
								{ fontSize: 14 * scale, lineHeight: 20 * scale },
							]}
						>
							{t('T_STORE_VIP_MANAGE_SUBSCRIPTION', { defaultValue: 'Manage Subscription ›' })}
						</Text>
					</Pressable>
				</View>

				<Text
					style={[
						styles.benefitsHeading,
						{ fontSize: 18 * scale, lineHeight: 24 * scale, marginVertical: 8 * scale },
					]}
				>
					{t('T_STORE_VIP_BENEFITS', { defaultValue: 'VIP CLUB BENEFITS' })}
				</Text>
				<View style={[styles.benefits, { gap: 10 * scale }]}>
					{BENEFITS.map(benefit => (
						<BenefitCard key={benefit.titleKey} benefit={benefit} isDesktop={false} scale={scale} />
					))}
				</View>

				<CtaButton
					label={t('T_STORE_VIP_GO_TO_CLUB', { defaultValue: 'Go to VIP Club ›' })}
					onPress={onGoToClub}
					size='medium'
					variant='gold'
					style={[styles.clubAction, { marginVertical: 12 * scale }]}
					labelStyle={{ fontSize: 18 * scale, fontWeight: '900', letterSpacing: 0.36 * scale }}
				/>
				<View style={[styles.disclosure, { borderRadius: 12 * scale, padding: 12 * scale }]}>
					<Text style={[styles.disclosureLabel, { fontSize: 12 * scale, lineHeight: 17 * scale }]}>
						{t('T_STORE_VIP_GOOGLE_PLAY_DISCLOSURE', {
							defaultValue:
								'Payment will be charged to your Google Play account at confirmation of purchase and will automatically renew every 30 days unless auto-renew is turned off. You can cancel at any time and keep access until the end of the current billing period. Manage your subscription in Google Play settings.',
						})}
					</Text>
				</View>
				<View style={[styles.legal, { columnGap: 12 * scale, marginTop: 12 * scale }]}>
					<Text style={[styles.legalLink, { fontSize: 12 * scale }]}>
						{t('T_TERMS_OF_USE', { defaultValue: 'Terms of Use' })}
					</Text>
					<Text style={[styles.legalLabel, { fontSize: 12 * scale }]}>|</Text>
					<Text style={[styles.legalLink, { fontSize: 12 * scale }]}>
						{t('T_PRIVACY_POLICY', { defaultValue: 'Privacy Policy' })}
					</Text>
				</View>
			</RedesignScrollView>
		</View>
	);
};

const DesktopVipContent = ({
	donation = false,
	onBuy,
	onGoToClub,
	scale,
	vipPackages,
}: Pick<Props, 'donation' | 'onBuy' | 'onGoToClub' | 'scale' | 'vipPackages'>) => {
	const packageScale = scale * 0.82;
	const { t } = useTranslation();

	return (
		<View style={styles.root}>
			<RedesignScrollView
				style={styles.scroll}
				contentContainerStyle={[
					styles.content,
					styles.desktop,
					{ padding: 15 * scale, paddingBottom: 20 * scale, rowGap: 15 * scale },
				]}
				indicatorTheme={SHOP_SCROLL_INDICATOR}
				reserveGutter={false}
			>
				<View style={[styles.desktopPackages, { columnGap: 20 * scale }]}>
					{vipPackages.slice(0, 3).map(pkg => (
						<View
							key={pkg.id}
							style={[
								styles.desktopPackage,
								{
									borderRadius: 16 * packageScale,
									paddingHorizontal: 32 * packageScale,
									paddingVertical: 36 * packageScale,
									rowGap: 8 * packageScale,
								},
							]}
						>
							<Text
								style={[
									styles.desktopPackageTitle,
									{ fontSize: 24 * packageScale, lineHeight: 27 * packageScale },
								]}
							>
								{vipPackageTitle(t, pkg)}
							</Text>
							<Image
								source={vipPackageArtwork(pkg)}
								resizeMode='contain'
								style={[
									styles.desktopPackageArt,
									(pkg.vipDurationDays ?? 0) >= 180 && styles.desktopPackageArtWide,
								]}
							/>
							<Text
								style={[
									styles.desktopPackagePrice,
									{ fontSize: 30 * packageScale, lineHeight: 36 * packageScale },
								]}
							>
								{formatPackagePrice(pkg)}
							</Text>
							<CtaButton
								label={
									donation
										? t('T_SITE_DONATION_DONATE', { defaultValue: 'DONATE' })
										: t('T_STORE_VIP_BUY', { defaultValue: 'BUY' })
								}
								onPress={() => onBuy(pkg)}
								size='medium'
								style={styles.packageBuyAction}
								surfaceStyle={{ minHeight: 44 * packageScale }}
								labelStyle={{ fontSize: 22 * packageScale, fontWeight: '900' }}
							/>
						</View>
					))}
				</View>

				<View style={[styles.desktopBenefitsHeading, { columnGap: 12 * scale }]}>
					<View style={styles.desktopBenefitsRule} />
					<Text
						style={[styles.desktopBenefitsTitle, { fontSize: 24 * scale, lineHeight: 27 * scale }]}
					>
						{t('T_STORE_VIP_BENEFITS', { defaultValue: 'VIP CLUB BENEFITS' })}
					</Text>
					<View style={styles.desktopBenefitsRule} />
				</View>
				<View style={[styles.benefits, { gap: 10 * scale }]}>
					{BENEFITS.map(benefit => (
						<BenefitCard key={benefit.titleKey} benefit={benefit} isDesktop scale={scale} />
					))}
				</View>
				{/* The club is the buyer's own VIP page; a donation has nothing to do there. */}
				{!donation ? (
					<CtaButton
						label={t('T_STORE_VIP_GO_TO_CLUB', { defaultValue: 'Go to VIP Club ›' })}
						onPress={onGoToClub}
						size='medium'
						variant='gold'
						style={styles.clubAction}
						surfaceStyle={{ borderRadius: 10 * scale, minHeight: 48 * scale }}
						labelStyle={{
							fontSize: 18 * scale,
							fontWeight: '900',
							letterSpacing: 0.36 * scale,
							lineHeight: 23.4 * scale,
						}}
					/>
				) : null}
			</RedesignScrollView>
		</View>
	);
};

const BenefitCard = ({
	benefit,
	isDesktop,
	scale,
}: {
	benefit: Benefit;
	isDesktop: boolean;
	scale: number;
}) => {
	const { t } = useTranslation();

	return (
		<View
			style={[
				styles.benefit,
				isDesktop ? styles.desktopBenefit : styles.mobileBenefit,
				{ borderRadius: 12 * scale, padding: 10 * scale, rowGap: 6 * scale },
			]}
		>
			<Image source={benefit.image} resizeMode='contain' style={styles.benefitImage} />
			<Text style={[styles.benefitTitle, { fontSize: 14 * scale, lineHeight: 16 * scale }]}>
				{t(benefit.titleKey, { defaultValue: benefit.defaultTitle })}
			</Text>
			<Text style={[styles.benefitDescription, { fontSize: 10 * scale, lineHeight: 14 * scale }]}>
				{t(benefit.descriptionKey, { defaultValue: benefit.defaultDescription })}
			</Text>
		</View>
	);
};

const styles = StyleSheet.create({
	root: { flex: 1, minHeight: 0, overflow: 'hidden' },
	scroll: { flex: 1, minHeight: 0 },
	content: { alignItems: 'stretch', flexGrow: 1 },
	mobile: { alignSelf: 'stretch' },
	desktop: { alignSelf: 'center', width: '100%' },
	hero: { alignItems: 'center', width: '100%' },
	heroImageFrame: { aspectRatio: 539 / 338, width: '100%' },
	heroImage: { height: '100%', width: '100%' },
	tagline: { color: '#FFE9A8', fontFamily: 'Sofia Sans', fontWeight: '500', textAlign: 'center' },
	subscription: {
		alignItems: 'center',
		backgroundColor: 'rgba(74, 9, 3, 0.9)',
		borderColor: '#E89200',
		borderWidth: 1,
	},
	priceRow: {
		alignItems: 'baseline',
		columnGap: 8,
		flexDirection: 'row',
		justifyContent: 'center',
	},
	price: { color: '#FFE9A8', fontFamily: 'Sofia Sans', fontWeight: '700' },
	period: { color: '#FFE9A8', fontFamily: 'Sofia Sans', fontWeight: '700' },
	renewal: { color: '#D9C4BC', fontFamily: 'Sofia Sans', fontWeight: '500', textAlign: 'center' },
	manageSubscription: { alignSelf: 'stretch', paddingVertical: 2 },
	subscribeAction: { alignSelf: 'stretch' },
	manageSubscriptionLabel: {
		color: '#FFE9A8',
		fontFamily: 'Sofia Sans',
		fontWeight: '500',
		textAlign: 'center',
	},
	benefitsHeading: {
		color: '#FFE9A8',
		fontFamily: 'Sofia Sans',
		fontWeight: '700',
		textAlign: 'center',
	},
	benefits: { flexDirection: 'row', flexWrap: 'wrap', justifyContent: 'center' },
	benefit: {
		alignItems: 'center',
		backgroundColor: '#330E07',
		borderColor: '#E89200',
		borderWidth: 1,
	},
	mobileBenefit: { flexGrow: 1, flexShrink: 1, width: '30%' },
	desktopBenefit: {
		aspectRatio: 1.08,
		flexGrow: 1,
		flexShrink: 1,
		justifyContent: 'center',
		width: '22%',
	},
	benefitImage: { aspectRatio: 1, maxWidth: '45%', width: '100%' },
	benefitTitle: {
		color: '#FFFF99',
		fontFamily: 'Sofia Sans',
		fontWeight: '600',
		textAlign: 'center',
	},
	benefitDescription: {
		color: '#D9C4BC',
		fontFamily: 'Sofia Sans',
		fontWeight: '500',
		textAlign: 'center',
	},
	clubAction: { alignSelf: 'stretch' },
	donationPackage: {
		alignItems: 'center',
		backgroundColor: 'rgba(46, 10, 4, 0.92)',
		borderColor: '#E35C11',
		borderWidth: 1,
		flexDirection: 'row',
	},
	donationPackageDetails: { alignItems: 'center', flex: 1 },
	desktopPackages: { alignItems: 'stretch', flexDirection: 'row', justifyContent: 'center' },
	desktopPackage: {
		alignItems: 'center',
		aspectRatio: 340 / 350,
		backgroundColor: 'rgba(74, 9, 3, 0.9)',
		borderColor: '#FFF908',
		borderWidth: 1,
		flex: 1,
		justifyContent: 'center',
		maxWidth: '48%',
		overflow: 'hidden',
	},
	desktopPackageTitle: {
		color: '#FFE9A8',
		fontFamily: 'Sofia Sans Condensed',
		fontWeight: '600',
		textAlign: 'center',
	},
	desktopPackageArt: { aspectRatio: 1, maxWidth: '48%', width: '100%' },
	// The multi-month exports are 482x296; the wider frame keeps their main medallion the same size.
	desktopPackageArtWide: { aspectRatio: 482 / 296, maxWidth: '80%' },
	desktopPackagePrice: { color: '#FFE9A8', fontFamily: 'Sofia Sans', fontWeight: '700' },
	packageBuyAction: { alignSelf: 'stretch' },
	desktopBenefitsHeading: { alignItems: 'center', flexDirection: 'row' },
	desktopBenefitsRule: { backgroundColor: '#E89200', flex: 1, height: 1 },
	desktopBenefitsTitle: {
		color: '#FFE9A8',
		fontFamily: 'Sofia Sans Condensed',
		fontWeight: '600',
		textAlign: 'center',
	},
	disclosure: { backgroundColor: 'rgba(33, 5, 3, 0.92)' },
	disclosureLabel: { color: '#D9C4BC', fontFamily: 'Sofia Sans', fontWeight: '500' },
	legal: { flexDirection: 'row', justifyContent: 'center' },
	legalLabel: { color: '#FFE9A8', fontFamily: 'Sofia Sans' },
	legalLink: { color: '#FFE9A8', fontFamily: 'Sofia Sans', textDecorationLine: 'underline' },
});

export default StoreVipContent;
